import React, { useEffect, useState } from 'react';
import { Input, Table, Button, DOMHelper, Stack, IconButton, SelectPicker, Divider } from 'rsuite';
import type { SortType } from 'rsuite';
import EditIcon from '@rsuite/icons/Edit';
import { BaseErrorModel, PagedResponseModel, PaginationFilterResult } from '@/models/core.model';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import FetchingSelect from '@/components/FetchingSelect';
import TablePagination from '@/components/TablePagination';
import { toastErrorMessage } from '@/utils/toasterMessage';
import ImageCell from '@/components/ImageCell';
import { EncyclopediaItem, EncyclopediaItemPaginationFilterRequest, EncyclopediaTypeList } from '@/models/encyclopedia-item.model';
import { getPagedEncyclopediaItems } from '@/services/encyclopedia-item.service';
import { getStoriesLookup } from '@/services/story.service';
import PageContent from '@/components/PageContent';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const EncyclopediaItemPage = () => {

  const [filterReq, setFilterReq] = useState<EncyclopediaItemPaginationFilterRequest>(new EncyclopediaItemPaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<EncyclopediaItem[]>([]);
  const [loading, setLoading] = useState(true);

  const nav = useNavigate();

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: EncyclopediaItemPaginationFilterRequest) => {
    setLoading(true);
    try {
      const result: PagedResponseModel<EncyclopediaItem> = await getPagedEncyclopediaItems(req);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ 
          ...pre, 
          pageNumber: result.pageNumber, 
          totalRecords: result.totalRecords, 
          pageSize: result.pageSize 
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching Encyclopedia Item:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre, 
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Button appearance="primary" onClick={() => nav(`/encyclopedia-items/new`)}>
          Add Encyclopedia Item
        </Button>
        <Stack spacing={6}>
          <FetchingSelect
            label="Story"
            fetchData={(text, page) => getStoriesLookup(text, page)}
            value={filterReq.storyId}
            onChange={dataKey => setFilterReq(pre => ({...pre, storyId: dataKey}))}
          />          
          <SelectPicker
            label="Type"
            data={EncyclopediaTypeList}
            searchable={false}
            value={filterReq.type ?? null}
            onChange={dataKey => setFilterReq(pre => ({ ...pre, type: dataKey ?? undefined }))} 
          />
          <Input 
            placeholder="Keyword" 
            value={filterReq.keyword ?? ''} 
            onChange={dataKey => setFilterReq(pre => ({...pre, keyword: dataKey}))}
          />
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
      >
        <Column flexGrow={0.5} align="center" >
          <HeaderCell>Image</HeaderCell>
          <ImageCell dataKey="imageUrl" />
        </Column>
        <Column flexGrow={2} align="left" sortable>
          <HeaderCell>Name</HeaderCell>
          <Cell dataKey="name"/>
        </Column>
        <Column flexGrow={1} align="center" sortable>
          <HeaderCell>Type</HeaderCell>
          <Cell dataKey="type">
            {(rowData: EncyclopediaItem) => EncyclopediaTypeList.findLast(t => t.value == rowData.type)?.label ?? ""}
          </Cell>
        </Column>
        <Column flexGrow={1} fixed="right" >
        <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {rowData => (         
              <IconButton onClick={() => nav(`/encyclopedia-items/${rowData.id}`)} icon={<EditIcon />} circle size="sm" />  
            )}
          </Cell>
        </Column>
      </Table>
      <Divider />
      <TablePagination 
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({...pre, pageNumber: dataKey}))}
        onChangeLimit={dataKey => setFilterReq(pre => ({...pre, pageSize: dataKey}))}
      />
    </PageContent>
  );
};

export default EncyclopediaItemPage;
